import { formatNumberForTable } from "@/Utils/formatFunctions";
import { useTrans } from "@/translations";

import { useMemo } from "react";

import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";

import type { ContributionSliceData } from "../types";

type Props = {
  hoverIndex: number | null;
  setHoverIndex: (value: number | null) => void;
  data: ContributionSliceData[];
};

export const TenantContributionGraph: React.FC<Props> = ({
  hoverIndex,
  setHoverIndex,
  data,
}) => {
  const { t } = useTrans();
  const hovered = useMemo(
    () => (hoverIndex != null ? data[hoverIndex] : null),
    [hoverIndex, data],
  );

  const total = useMemo(
    () => data.reduce((sum, d) => sum + d.value, 0),
    [data],
  );

  const hoveredValue = useMemo(() => {
    if (hovered) {
      return `${formatNumberForTable(hovered.pct, { numDecimals: 0 })}%`;
    }

    return formatNumberForTable(total, { numDecimals: 0 });
  }, [hovered, total]);

  return (
    <div className="relative h-[200px] w-[200px] flex-none">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="label"
            innerRadius="70%"
            outerRadius="100%"
            paddingAngle={1}
            stroke="none"
            isAnimationActive={false}
            onMouseEnter={(_, index) => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex(null)}
          >
            {data.map((d, i) => (
              <Cell
                key={d.label}
                fill={d.color}
                opacity={hoverIndex == null || hoverIndex === i ? 1 : 0.3}
                style={{ cursor: "pointer", transition: "opacity .12s" }}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-signifier text-[22px] leading-none">
          {hoveredValue}
        </span>
        <span className="text-grey6 mt-[4.95px] max-w-[90.75px] truncate text-[10.5px]">
          {hovered ? hovered.label : t("homePage:tenantContributionKgTotal")}
        </span>
      </div>
    </div>
  );
};
